'use client';

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { HintDisclosure } from 'components/Widget/shared/HintDisclosure';
import { cn } from 'utils/styles';
import { HINT_VISIBLE } from './constants';

export interface CalculatorInputAffix {
  symbol: '$' | '%';
  position: 'prefix' | 'suffix';
}

interface CalculatorInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
  /** A unit marker held inside the field. Absent means no marker: a count field is not money. */
  affix?: CalculatorInputAffix;
  /**
   * A trailing unit read as words rather than a symbol, such as `/ month`. Separate from `affix`
   * so a field can carry both, and because its width has to be measured rather than assumed.
   */
  suffixLabel?: string;
  /** Puts the hint behind an info toggle instead of showing it under the field. */
  collapsibleHint?: boolean;
}

export const CalculatorInput = ({
  ref,
  id,
  label,
  hint,
  error,
  invalid = false,
  affix,
  suffixLabel,
  collapsibleHint = false,
  className,
  placeholder,
  onFocus,
  onBlur,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...inputProps
}: CalculatorInputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const hasError = invalid && Boolean(error);
  // A toggle with nothing to control would leave `aria-controls` dangling.
  const hasHintToggle = collapsibleHint && Boolean(hint);
  const describedBy =
    [ariaDescribedBy, hasError ? errorId : undefined, hint ? hintId : undefined]
      .filter(value => value !== undefined)
      .join(' ') || undefined;
  const labelElement = (
    <label htmlFor={inputId} className="block text-body-saans">
      {label}
    </label>
  );

  return (
    <div className="w-full">
      {hasHintToggle ? (
        <div className="flex items-center gap-2">
          {labelElement}
          <HintDisclosure hintId={hintId} label={label} hint={hint} />
        </div>
      ) : (
        labelElement
      )}
      <div className="relative mt-3">
        <input
          {...inputProps}
          ref={ref}
          id={inputId}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={invalid || ariaInvalid || undefined}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            'h-16 w-full rounded-lg border border-content-secondary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow',
            'placeholder:text-content-secondary hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:ring-[6px] focus:ring-bg-tertiary',
            'disabled:cursor-not-allowed disabled:bg-accent-grey-100 disabled:text-content-disabled-primary disabled:hover:ring-transparent',
            affix?.position === 'prefix' && 'pl-10',
            affix?.position === 'suffix' && 'pr-10',
            // Wide enough for the longest unit word, so a long value never runs under the suffix.
            suffixLabel && 'pr-24',
            invalid &&
              'border-util-alert-500 bg-util-alert-100 hover:ring-util-alert-200 focus:border-util-alert-500 focus:ring-util-alert-200',
            className,
          )}
        />
        {affix && (
          <span
            aria-hidden
            className={
              affix.position === 'prefix'
                ? 'pointer-events-none absolute top-1/2 left-6 -translate-y-1/2 text-body-saans'
                : 'pointer-events-none absolute top-1/2 right-6 -translate-y-1/2 text-body-saans'
            }>
            {affix.symbol}
          </span>
        )}
        {suffixLabel && (
          <span
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-6 -translate-y-1/2 text-body-saans text-content-secondary">
            {suffixLabel}
          </span>
        )}
      </div>
      {hasError && (
        <p id={errorId} role="alert" aria-live="assertive" className="mt-3 text-body-saans text-util-alert-500">
          {error}
        </p>
      )}
      {/* The toggle carries its own copy of the hint, so rendering it here too would duplicate the id. */}
      {hint && !hasHintToggle && (
        <p id={hintId} className={HINT_VISIBLE}>
          {hint}
        </p>
      )}
    </div>
  );
};
